"use client"

import React, { useEffect, useMemo, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { Campaign, SearchParams } from "@ub/fact-finder-client"
import { useTranslations } from "next-intl"

import { SearchResult } from "@/lib/factfinder/factfinder-interfaces"
import { DEFAULT_MARKET_CHANNEL, type MarketChannel } from "@/lib/market/market-channel"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { GuidedSellingContainer } from "@/components/guided-selling/guided-selling-container"
import { FilterButtonClient } from "@/components/search-and-category/FilterButtonClient"
import FacetList from "@/components/search-and-category/FacetList"
import SearchResultContainer from "@/components/search-and-category/SearchResultContainer"
import FallbackCard from "@/components/common/FallbackCard"
import { ApplicationSettings } from "@/lib/settings/application-settings"
import { UbGlobalGuidedSellingStoryblok, UbSearchResultsStoryblok } from "@/components/storyblok/component-types"
import { resolveListingApiUrl } from "@/components/storyblok/listing-api-client"

interface SearchListingResponse {
  searchresult?: SearchResult
  ffSearchParams?: SearchParams
  isFiltered: boolean
  redirectTo?: string
  error?: string
}

interface UBSearchResultsClientProps {
  blok: UbSearchResultsStoryblok
  query: string
  market: string
  channel?: MarketChannel
  sbLocale: string
  isoLocale: string
  defaultFacetsToHide: string[]
  applicationSettings: ApplicationSettings
  noHitsTopContent?: React.ReactNode
  noHitsSearchBox?: React.ReactNode
  noHitsBottomContent?: React.ReactNode
  guidedSellingSettings?: UbGlobalGuidedSellingStoryblok
  guidedSellingSummaryTop?: React.ReactNode
  guidedSellingSummaryBottom?: React.ReactNode
}

function getSearchQueryFromPath(pathname: string | null): string {
  if (!pathname) {
    return ""
  }

  const segments = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  const searchSegmentIndex = segments.findIndex((segment) => segment.toLowerCase() === "search")
  if (searchSegmentIndex === -1 || searchSegmentIndex >= segments.length - 1) {
    return ""
  }

  return segments
    .slice(searchSegmentIndex + 1)
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
    .join("/")
}

function SearchBreadCrumbs({ query }: { query: string }) {
  const t = useTranslations("storefront")
  const queryDisplay = query || "(-)"

  return (
    <div className="flex flex-row flex-wrap gap-2 text-xs leading-none">
      <div className="flex flex-row gap-2">
        <a href="/">{t("breadcrumbs.home")}</a>
        <span>&gt;</span>
      </div>
      <div className="flex flex-row gap-2">
        <b>{t("breadcrumbs.searchresult", { query: queryDisplay })}</b>
      </div>
    </div>
  )
}

export default function UBSearchResultsClient({
  blok,
  query,
  market,
  channel = DEFAULT_MARKET_CHANNEL,
  sbLocale,
  isoLocale,
  defaultFacetsToHide,
  applicationSettings,
  noHitsTopContent,
  noHitsSearchBox,
  noHitsBottomContent,
  guidedSellingSettings,
  guidedSellingSummaryTop,
  guidedSellingSummaryBottom,
}: UBSearchResultsClientProps) {
  const t = useTranslations("storefront")
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setIsLoading } = useUbStorefrontState()
  const [data, setData] = useState<SearchListingResponse | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [queryFromPath, setQueryFromPath] = useState("")
  const [isPathQueryResolved, setIsPathQueryResolved] = useState(false)

  const queryString = searchParams.toString()
  const queryFromSearchParams = searchParams.get("query") ?? ""
  const hasExplicitQuery = !!(queryFromSearchParams || query)
  const effectiveQuery = queryFromSearchParams || query || queryFromPath
  const queryDisplay = effectiveQuery || "(-)"
  const templateBlok = blok.resultDisplayType[0]
  const allowDebugMode = applicationSettings.allowDebugMode ?? false
  const hideAddToBasket = applicationSettings.hideAddToBasketButton ?? false

  useEffect(() => {
    setQueryFromPath(getSearchQueryFromPath(window.location.pathname))
    setIsPathQueryResolved(true)
  }, [pathname])

  useEffect(() => {
    if (!effectiveQuery) {
      if (!hasExplicitQuery && !isPathQueryResolved) {
        setIsLoading(true)
        return
      }

      setData({ isFiltered: false })
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    const apiSearchParams = new URLSearchParams(queryString)
    apiSearchParams.set("market", market)
    apiSearchParams.set("channel", channel)
    apiSearchParams.set("query", effectiveQuery)

    setIsLoading(true)
    setError(undefined)

    resolveListingApiUrl("search", apiSearchParams, { sbLocale, channel, signal: controller.signal })
      .then((url) =>
        fetch(url, {
          signal: controller.signal,
        })
      )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text())
        }
        return response.json() as Promise<SearchListingResponse>
      })
      .then((result) => {
        if (result.redirectTo) {
          window.location.href = result.redirectTo
          return
        }
        setData(result)
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : "Unable to load search results")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [channel, effectiveQuery, hasExplicitQuery, isPathQueryResolved, market, queryString, sbLocale, setIsLoading])

  const guidedSellingCampaign: Campaign | undefined = useMemo(
    () =>
      blok.showGuidedSellingBlock
        ? (data?.searchresult?.campaigns?.find((campaign) => campaign.flavour === "ADVISOR") ?? undefined)
        : undefined,
    [blok.showGuidedSellingBlock, data?.searchresult?.campaigns]
  )

  if (error) {
    return (
      <>
        <SearchBreadCrumbs query={effectiveQuery} />
        <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm">{t("category.products.empty")}</div>
      </>
    )
  }

  if (!data) {
    return (
      <>
        <SearchBreadCrumbs query={effectiveQuery} />
        <FallbackCard className="min-h-480 bg-gray-100" />
      </>
    )
  }

  const searchresult = data.searchresult
  const hitcount = searchresult?.hitcount ?? 0

  if (!hitcount) {
    return (
      <div className="mb-16 flex flex-col gap-4">
        <SearchBreadCrumbs query={effectiveQuery} />
        <h1 className="text-xl font-bold">{t("search.result_no_hits")}</h1>
        <div className="flex flex-col gap-8">
          {noHitsTopContent}
          <p>{t("search.result_empty", { query: queryDisplay })}</p>
          {noHitsSearchBox}
          {noHitsBottomContent}
        </div>
      </div>
    )
  }

  return (
    <>
      <SearchBreadCrumbs query={effectiveQuery} />
      <div className="flex flex-row gap-4 pb-4">
        <FacetList
          facetManager={undefined}
          isoLocale={isoLocale}
          sbLocale={sbLocale}
          defaultFacetsToHide={defaultFacetsToHide}
          facets={searchresult?.facets ?? []}
          query={effectiveQuery}
          hitcount={hitcount}
          isFiltered={data.isFiltered}
          ffSearchParams={data.ffSearchParams}
        />

        <div className="@container flex w-full flex-col gap-y-4 pt-1">
          <div className="text-xl font-bold">{t("search.result_headline", { query: queryDisplay, hitcount })}</div>
          {guidedSellingCampaign &&
            guidedSellingSettings?.show_guided_selling_block &&
            guidedSellingSettings.label_breadcrumb_start &&
            guidedSellingSettings.label_breadcrumb_end &&
            guidedSellingSettings.label_from_to &&
            guidedSellingSettings.label_or &&
            guidedSellingSettings.label_btn_confirm &&
            guidedSellingSettings.label_btn_reset &&
            guidedSellingSettings.label_btn_open && (
              <GuidedSellingContainer
                campaign={guidedSellingCampaign}
                label_breadcrumb_start={guidedSellingSettings.label_breadcrumb_start}
                label_breadcrumb_end={guidedSellingSettings.label_breadcrumb_end}
                label_from_to={guidedSellingSettings.label_from_to}
                label_or={guidedSellingSettings.label_or}
                label_btn_confirm={guidedSellingSettings.label_btn_confirm}
                label_btn_reset={guidedSellingSettings.label_btn_reset}
                label_btn_open={guidedSellingSettings.label_btn_open}
                blok_summary_top={guidedSellingSummaryTop}
                blok_summary_bottom={guidedSellingSummaryBottom}
                trackingPageType="search"
                totalHits={hitcount}
                ffSearchParams={searchresult?.effectiveFFParams}
                showCloseButton={true}
                defaultClosed={true}
              />
            )}
          <div className="flex flex-row items-center justify-center bg-transparent lg:hidden">
            <FilterButtonClient />
          </div>
          <SearchResultContainer
            isB2B={true}
            allowDebugMode={allowDebugMode}
            hideAddToBasket={hideAddToBasket}
            inStoryblock={false}
            tracking="search"
            sbLocale={sbLocale}
            channel={channel}
            isoLocale={isoLocale}
            products={searchresult?.hits ?? []}
            templateBlok={templateBlok}
            productsTotal={hitcount}
            viewItemListPageType="search"
            searchTerm={effectiveQuery}
            campaignId={guidedSellingCampaign?.id}
          />
        </div>
      </div>
    </>
  )
}
